
import { IText, util, Point, Control, Object as FabricObject } from 'fabric';

// --- From textWarp_copy.ts: For integration with Fabric ---

type FabricObjectWithSpline = IText & {
    splineGridSize: number;
    srcPts: { x: number, y: number }[];
    dstPts: { x: number, y: number }[];
    isSplineWarped: () => boolean;
    _render_original_spline?: (ctx: CanvasRenderingContext2D) => void;
    _warpTexture_spline: HTMLCanvasElement | null;
    textureDirty_spline: boolean;
    set_original_spline?(key: any, value?: any): FabricObjectWithSpline;
    setCoords_original_spline?(): void;
};

type CustomSplineControl = Control & {
    corner: string;
};

function drawTexturedTriangle(
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    p0: Point, p1: Point, p2: Point,
    uv0: Point, uv1: Point, uv2: Point
) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.closePath();
    ctx.clip();

    const denom = uv0.x * (uv1.y - uv2.y) + uv1.x * (uv2.y - uv0.y) + uv2.x * (uv0.y - uv1.y);
    if (denom === 0) {
        ctx.restore();
        return;
    }

    const a = (p0.x * (uv1.y - uv2.y) + p1.x * (uv2.y - uv0.y) + p2.x * (uv0.y - uv1.y)) / denom;
    const b = (p0.y * (uv1.y - uv2.y) + p1.y * (uv2.y - uv0.y) + p2.y * (uv0.y - uv1.y)) / denom;
    const c = (p0.x * (uv2.x - uv1.x) + p1.x * (uv0.x - uv2.x) + p2.x * (uv1.x - uv0.x)) / denom;
    const d = (p0.y * (uv2.x - uv1.x) + p1.y * (uv0.x - uv2.x) + p2.y * (uv1.x - uv0.x)) / denom;
    const e = p0.x - a * uv0.x - c * uv0.y;
    const f = p0.y - b * uv0.x - d * uv0.y;

    ctx.transform(a, b, c, d, e, f);
    ctx.drawImage(image, 0, 0);
    ctx.restore();
}

function renderSplineGridPointIcon(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: any, fabricObject: any) {
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(util.degreesToRadians(fabricObject.angle));
    ctx.beginPath();
    ctx.arc(0, 0, 6, 0, 2 * Math.PI);
    ctx.fillStyle = "blue";
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
}

function splineGridPointActionHandler(eventData: MouseEvent, transform: any, x: number, y: number) {
    const target = transform.target as FabricObjectWithSpline;
    const corner = transform.corner;
    const [_, index_str] = corner.split('_');
    const index = parseInt(index_str);

    const point = util.transformPoint(
      new Point(x, y),
      util.invertTransform(target.calcTransformMatrix())
    );
    
    target.dstPts[index].x = point.x;
    target.dstPts[index].y = point.y;
    
    return true;
}

function splineGridPointPositionHandler(this: CustomSplineControl, dim: any, finalMatrix: any, fabricObject: any) {
    const index = parseInt(this.corner.split('_')[1]);
    const point = (fabricObject as FabricObjectWithSpline).dstPts[index];
    return util.transformPoint(new Point(point.x, point.y), fabricObject.calcTransformMatrix());
}

function cloneControls(controls: { [key: string]: Control }): { [key: string]: Control } {
    const newControls: { [key: string]: Control } = {};
    for (const key in controls) {
        if (controls.hasOwnProperty(key)) {
            const control = controls[key];
            newControls[key] = new Control(control as any);
        }
    }
    return newControls;
}

// --- From A-Spline.html: TPS Math ---

function U(r: number) {
    if (r === 0) return 0;
    return r * r * Math.log(r);
}

function computeTPS(srcPts: {x:number, y:number}[], dstPts: {x:number, y:number}[]) {
    const n = srcPts.length;
    const K: number[][] = Array.from({length:n}, ()=>Array(n).fill(0));
    const P: number[][] = Array.from({length:n}, ()=>[0,0,1]);

    for(let i=0;i<n;i++){
        P[i][0] = srcPts[i].x;
        P[i][1] = srcPts[i].y;
        for(let j=0;j<n;j++){
            const dx = srcPts[i].x - srcPts[j].x;
            const dy = srcPts[i].y - srcPts[j].y;
            K[i][j] = U(Math.sqrt(dx*dx + dy*dy));
        }
    }

    const L: number[][] = Array.from({length:n+3}, ()=>Array(n+3).fill(0));

    for(let i=0;i<n;i++) for(let j=0;j<n;j++) L[i][j] = K[i][j];
    for(let i=0;i<n;i++){
        L[i][n]   = P[i][0]; L[i][n+1] = P[i][1]; L[i][n+2] = 1;
        L[n][i]   = P[i][0]; L[n+1][i] = P[i][1]; L[n+2][i] = 1;
    }

    const Yx = Array(n+3).fill(0);
    const Yy = Array(n+3).fill(0);
    for(let i=0;i<n;i++){ Yx[i] = dstPts[i].x; Yy[i] = dstPts[i].y; }

    function solve(L: number[][], Y: number[]) {
        const dim = Y.length;
        const A = L.map((r,i)=>r.slice().concat([Y[i]]));

        for(let i=0;i<dim;i++){
            let maxRow = i;
            for(let r=i+1;r<dim;r++) if(Math.abs(A[r][i]) > Math.abs(A[maxRow][i])) maxRow = r;
            if (maxRow !== i) [A[i], A[maxRow]] = [A[maxRow], A[i]];
            for(let r=i+1;r<dim;r++){
                const m = A[r][i] / A[i][i];
                for(let c=i;c<=dim;c++) A[r][c] -= A[i][c] * m;
            }
        }

        const W = Array(dim).fill(0);
        for(let i=dim-1;i>=0;i--){
            let sum = A[i][dim];
            for(let c=i+1;c<dim;c++) sum -= A[i][c] * W[c];
            W[i] = sum / A[i][i];
        }
        return W;
    }

    return {Wx: solve(L,Yx), Wy: solve(L,Yy)};
}

function warpPoint(x: number, y: number, srcPts: {x:number, y:number}[], Wx: number[], Wy: number[]) {
    const n = srcPts.length;
    let resultX = Wx[n] * x + Wx[n+1] * y + Wx[n+2];
    let resultY = Wy[n] * x + Wy[n+1] * y + Wy[n+2];
    for(let i=0;i<n;i++){
        const dx = x - srcPts[i].x;
        const dy = y - srcPts[i].y;
        const r = Math.sqrt(dx*dx + dy*dy);
        resultX += Wx[i] * U(r);
        resultY += Wy[i] * U(r);
    }
    return {x:resultX, y:resultY};
}

// --- Main Fabric Integration ---

function customSplineRender(this: FabricObjectWithSpline, ctx: CanvasRenderingContext2D) {
    if (!this.isSplineWarped()) {
        this._render_original_spline?.(ctx);
        return;
    }

    if (this.textureDirty_spline || !this._warpTexture_spline) {
        const zoom = this.canvas ? this.canvas.getZoom() : 1;
        const scaleX = this.scaleX * zoom;
        const scaleY = this.scaleY * zoom;

        const offscreenCanvas = util.createCanvasElement();
        offscreenCanvas.width = Math.ceil(this.width * scaleX);
        offscreenCanvas.height = Math.ceil(this.height * scaleY);
        const offscreenCtx = offscreenCanvas.getContext('2d')!;
        
        offscreenCtx.scale(scaleX, scaleY);
        offscreenCtx.translate(this.width / 2, this.height / 2);
        this._render_original_spline?.(offscreenCtx);
        this._warpTexture_spline = offscreenCanvas;
        this.textureDirty_spline = false;
    }

    const texture = this._warpTexture_spline;
    if (!texture) return;
    
    const { Wx, Wy } = computeTPS(this.srcPts, this.dstPts);

    const meshDetail = 10; // How many quads
    const { width, height } = this;

    for (let r = 0; r < meshDetail; r++) {
        for (let c = 0; c < meshDetail; c++) {
            const u0 = c / meshDetail, u1 = (c + 1) / meshDetail;
            const v0 = r / meshDetail, v1 = (r + 1) / meshDetail;
            
            const x0 = -width/2 + u0 * width, y0 = -height/2 + v0 * height;
            const x1 = -width/2 + u1 * width, y1 = -height/2 + v0 * height;
            const x2 = -width/2 + u1 * width, y2 = -height/2 + v1 * height;
            const x3 = -width/2 + u0 * width, y3 = -height/2 + v1 * height;

            const p0_w = warpPoint(x0, y0, this.srcPts, Wx, Wy);
            const p1_w = warpPoint(x1, y1, this.srcPts, Wx, Wy);
            const p2_w = warpPoint(x2, y2, this.srcPts, Wx, Wy);
            const p3_w = warpPoint(x3, y3, this.srcPts, Wx, Wy);

            const p0 = new Point(p0_w.x, p0_w.y);
            const p1 = new Point(p1_w.x, p1_w.y);
            const p2 = new Point(p2_w.x, p2_w.y);
            const p3 = new Point(p3_w.x, p3_w.y);

            const texW = texture.width, texH = texture.height;
            const uv0 = new Point(u0 * texW, v0 * texH);
            const uv1 = new Point(u1 * texW, v0 * texH);
            const uv2 = new Point(u1 * texW, v1 * texH);
            const uv3 = new Point(u0 * texW, v1 * texH);

            drawTexturedTriangle(ctx, texture, p0, p1, p3, uv0, uv1, uv3);
            drawTexturedTriangle(ctx, texture, p1, p2, p3, uv1, uv2, uv3);
        }
    }
}


export function enableSplineWarp(textObject: IText, gridSize: number = 3, srcPts?: {x:number, y:number}[], dstPts?: {x:number, y:number}[]) {
    const obj = textObject as FabricObjectWithSpline;

    if (!obj._render_original_spline) {
        obj._render_original_spline = obj._render.bind(obj);
        obj._render = customSplineRender as any;

        obj.set_original_spline = obj.set.bind(obj);
        obj.set = function(this: FabricObjectWithSpline, key: any, value?: any) {
            const watchedProps = ['text', 'fontSize', 'fontFamily', 'fontWeight', 'fill', 'stroke', 'strokeWidth', 'textBackgroundColor'];
            let dirty = false;
            if (typeof key === 'object') {
                for (const k in key) if (watchedProps.includes(k)) { dirty = true; break; }
            } else if (watchedProps.includes(key)) {
                dirty = true;
            }
            if (dirty) this.textureDirty_spline = true;
            this.set_original_spline?.(key, value);
            return this;
        }.bind(obj);
    }

    if (obj.splineGridSize !== gridSize) {
        obj.splineGridSize = gridSize;
        obj.srcPts = [];
        obj.dstPts = [];
        const w = obj.width;
        const h = obj.height;

        for (let r = 0; r < gridSize; r++) {
            for (let c = 0; c < gridSize; c++) {
                if (srcPts && dstPts && srcPts.length > 0 && dstPts.length > 0) {
                    obj.srcPts.push(srcPts[r * gridSize + c]);
                    obj.dstPts.push(dstPts[r * gridSize + c]);
                } else {
                    const x = -w/2 + (c / (gridSize - 1)) * w;
                    const y = -h/2 + (r / (gridSize - 1)) * h;
                    obj.srcPts.push({ x, y });
                    obj.dstPts.push({ x, y });
                }
            }
        }
    } else if (srcPts && dstPts && srcPts.length > 0 && dstPts.length > 0) {
        obj.srcPts = srcPts;
        obj.dstPts = dstPts;
    }

    obj.textureDirty_spline = true;
    obj.isSplineWarped = () => !!obj.splineGridSize;
    obj.controls = cloneControls(FabricObject.prototype.controls);

    for (let i = 0; i < obj.dstPts.length; i++) {
        const cornerName = `spline_${i}`;
        obj.controls[cornerName] = new Control({
            positionHandler: splineGridPointPositionHandler,
            actionHandler: splineGridPointActionHandler,
            render: renderSplineGridPointIcon,
            corner: cornerName,
        } as any);
    }
    
    obj.setControlsVisibility?.({ ml: false, mr: false, mt: false, mb: false });
    obj.objectCaching = false;
    textObject.dirty = true;
    textObject.canvas?.requestRenderAll();
}

export function disableSplineWarp(textObject: IText) {
    const obj = textObject as FabricObjectWithSpline;

    if (obj._render_original_spline) {
        obj._render = obj._render_original_spline;
        delete obj._render_original_spline;
    }
    if (obj.set_original_spline) {
        obj.set = obj.set_original_spline;
        delete obj.set_original_spline;
    }

    if (obj.controls) {
        Object.keys(obj.controls).forEach(key => {
            if (key.startsWith('spline_')) delete obj.controls[key];
        });
    }

    obj.splineGridSize = 0;
    obj.isSplineWarped = () => false;
    obj.objectCaching = true;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}
